/**
 * The country flag shown beside a language in the storefront switcher,
 * as a lowercase ISO 3166-1 alpha-2 code.
 *
 * Most languages take the flag of the country the language comes from
 * (es → Spain, pt → Portugal). Chinese depends on the script, not the
 * base code (PD-16672, owner decision 2026-09-30): Simplified Chinese
 * takes China's flag, and Traditional Chinese takes Hong Kong's, because
 * Traditional is the script of Hong Kong, Taiwan and Macau rather than of
 * mainland China. A bare country code (`us`, `cn`) passes through.
 * Returns null for anything without a flag.
 */
// A Map, not an object literal, so codes like `constructor` never resolve
// to an Object.prototype member.
const LANGUAGE_FLAGS = new Map<string, string>([
	['en', 'us'],
	['ko', 'kr'],
	['ja', 'jp'],
	['hi', 'in'],
	['bn', 'bd'],
	['ru', 'ru'],
	['es', 'es'],
	['pt', 'pt'],
]);

const KNOWN_FLAGS = new Set([
	'us',
	'cn',
	'hk',
	'kr',
	'jp',
	'in',
	'bd',
	'ru',
	'es',
	'pt',
]);

// Regions and scripts that mean Traditional Chinese.
const TRADITIONAL_CHINESE_PARTS = new Set(['hant', 'tw', 'hk', 'mo']);

export function flagCountryFor(code: string | null | undefined): string | null {
	if (code == null) {
		return null;
	}
	const parts = code.trim().toLowerCase().split(/[-_]/);
	const base = parts[0] ?? '';
	if (base === '') {
		return null;
	}
	if (base === 'zh') {
		return parts.slice(1).some((part) => TRADITIONAL_CHINESE_PARTS.has(part))
			? 'hk'
			: 'cn';
	}
	const fromLanguage = LANGUAGE_FLAGS.get(base);
	if (fromLanguage != null) {
		return fromLanguage;
	}
	return KNOWN_FLAGS.has(base) ? base : null;
}
